/** Internal estimates in USD. Null means unknown, never a verified zero. */
export const COST_FIELDS = [
  ["runpodPerVideo", "RunPod InfiniteTalk / video", "AI services"],
  ["fishPerMillionBytes", "Fish speech / 1M UTF-8 bytes", "AI services"],
  ["imageEditPerRequest", "Background composition / request", "AI services"],
  ["preparationInputTokens", "Analysis + script input tokens allocated / video", "Preparation"],
  ["preparationOutputTokens", "Analysis + script output tokens allocated / video", "Preparation"],
  ["voiceCloneAllocation", "Voice cloning allocated USD / video", "Preparation"],
  ["voicePreviewAllocation", "Voice previews allocated USD / video", "Preparation"],
  ["scrapingAllocation", "Scraping / external data USD / video", "Preparation"],
  ["postProcessingAllocation", "Cutout, editing, subtitles, music USD / video", "Preparation"],
  ["moderationAllocation", "Moderation / licensing USD / video", "Preparation"],
  ["storageGb", "All retained inputs, audio and outputs GB / video", "Storage & delivery"],
  ["retentionMonths", "Storage months to budget (does not auto-delete files)", "Storage & delivery"],
  ["storagePerGbMonth", "Storage USD / GB-month", "Storage & delivery"],
  ["writesPerVideo", "Storage Class A operations / video", "Storage & delivery"],
  ["writesPerMillion", "Class A USD / 1M operations", "Storage & delivery"],
  ["readsPerVideo", "Storage Class B operations / video", "Storage & delivery"],
  ["readsPerMillion", "Class B USD / 1M operations", "Storage & delivery"],
  ["deliveryGb", "Download / delivery GB allocated / video", "Storage & delivery"],
  ["deliveryPerGb", "Delivery USD / GB", "Storage & delivery"],
  ["workerRequests", "Backend requests / video", "Infrastructure"],
  ["workerPerMillionRequests", "Backend USD / 1M requests", "Infrastructure"],
  ["workerCpuMs", "Backend CPU milliseconds / video", "Infrastructure"],
  ["workerPerMillionCpuMs", "Backend USD / 1M CPU milliseconds", "Infrastructure"],
  ["queueRequests", "Queue messages / video", "Infrastructure"],
  ["queuePerMillion", "Queue USD / 1M messages", "Infrastructure"],
  ["redisAllocation", "Redis variable USD allocated / video", "Infrastructure"],
  ["databaseAllocation", "Database variable USD allocated / video", "Infrastructure"],
  ["monthlyFixedUsd", "Total fixed monthly USD: hosting, database, auth, monitoring, domain, support", "Infrastructure"],
  ["monthlyVideos", "Expected delivered videos / month", "Infrastructure"],
  ["failurePercent", "Failed paid attempts (% of all attempts)", "Risk & payment"],
  ["paymentPercent", "Payment processor percentage fee", "Risk & payment"],
  ["paymentFixedPerTopup", "Fixed payment fee USD / wallet top-up", "Risk & payment"],
  ["typicalTopupUsd", "Typical wallet top-up amount USD", "Risk & payment"],
  ["fxPercent", "Additional cross-border / FX percentage fee", "Risk & payment"],
  ["otherAllocation", "Other variable USD / video", "Infrastructure"],
] as const;

export type CostKey = (typeof COST_FIELDS)[number][0];
export type CostSettings = Record<CostKey, number | null>;
export interface CostLine { key: string; label: string; usd: number; basis: "estimate" | "allocation" | "reserve" }
export interface CostBreakdown {
  version: 1; estimatedAt: string; lines: CostLine[]; missing: CostKey[];
  baseCostUsd: number; paymentCostUsd: number; totalCostUsd: number;
  customerPriceUsd: number; estimatedProfitUsd: number; complete: boolean;
  settings: CostSettings;
}

/** Hypothetical sale only; never changes persisted pricing or wallet balances. */
export function simulateSale(baseCostUsd: number, priceUsd: number, settings: CostSettings) {
  const s = costSettings(settings);
  if (![baseCostUsd, priceUsd].every(n => Number.isFinite(n) && n >= 0 && n <= 1e12)) throw new Error("Invalid scenario amount");
  const feeRatio = (s.paymentPercent ?? 0) / 100 + (s.fxPercent ?? 0) / 100 + (s.paymentFixedPerTopup ?? 0) / (s.typicalTopupUsd || 1);
  const paymentUsd = priceUsd * feeRatio;
  const profitUsd = priceUsd - baseCostUsd - paymentUsd;
  return { paymentUsd, totalCostUsd: baseCostUsd + paymentUsd, profitUsd,
    marginPercent: priceUsd > 0 ? profitUsd / priceUsd * 100 : null,
    breakEvenUsd: Math.ceil(baseCostUsd / (1 - feeRatio) * 100 - 1e-9) / 100 };
}

/** Provider-reported amount is separate from every estimate/allocation. */
export function providerReportedCostCents(value: unknown): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return undefined;
  const cents = Math.max(0, Math.ceil(value * 100 - 1e-9));
  return Number.isSafeInteger(cents) ? cents : undefined;
}

export function costSettings(raw: unknown, fallbacks: Partial<CostSettings> = {}): CostSettings {
  const input = raw && typeof raw === "object" && !Array.isArray(raw) ? raw as Record<string, unknown> : {};
  const result = {} as CostSettings;
  for (const [key] of COST_FIELDS) {
    const value = Object.hasOwn(input, key) ? input[key] : fallbacks[key] ?? null;
    if (value !== null && (typeof value !== "number" || !Number.isFinite(value) || value < 0 || value > 1e12)) throw new Error(`Invalid cost setting: ${key}`);
    result[key] = value as number | null;
  }
  if ((result.failurePercent ?? 0) >= 100) throw new Error("Failure percentage must be below 100");
  if ((result.paymentFixedPerTopup ?? 0) > 0 && !(result.typicalTopupUsd && result.typicalTopupUsd > 0)) throw new Error("A typical top-up amount is required to allocate the fixed payment fee");
  if ((result.monthlyFixedUsd ?? 0) > 0 && !(result.monthlyVideos && result.monthlyVideos > 0)) throw new Error("Expected monthly videos must be positive when monthly costs are entered");
  const ratio = (result.paymentPercent ?? 0) / 100 + (result.fxPercent ?? 0) / 100 + (result.paymentFixedPerTopup ?? 0) / (result.typicalTopupUsd || 1);
  if (ratio >= 1) throw new Error("Combined payment fees must be below 100% of revenue");
  return result;
}

export function calculateFullCost(input: {
  providerLines: CostLine[]; settings: CostSettings; markupPercent: number; minimumPriceCents: number;
  inputTokenRate: number; outputTokenRate: number; applyPreparation?: boolean;
}): CostBreakdown {
  const s = costSettings(input.settings);
  const lines = input.providerLines.map(line => ({ ...line }));
  if (lines.some(line => !Number.isFinite(line.usd) || line.usd < 0)) throw new Error("Invalid provider cost");
  if (!Number.isFinite(input.markupPercent) || input.markupPercent < 0 || !Number.isFinite(input.minimumPriceCents) || input.minimumPriceCents < 0) throw new Error("Invalid selling price parameters");
  if (![input.inputTokenRate, input.outputTokenRate].every(rate => Number.isFinite(rate) && rate >= 0)) throw new Error("Invalid LLM cost rates");
  const value = (key: CostKey) => s[key] ?? 0;
  const add = (key: string, label: string, usd: number, basis: CostLine["basis"] = "allocation") => lines.push({ key, label, usd, basis });
  if (input.applyPreparation !== false) {
    add("llm", "Product analysis + script allocation", (value("preparationInputTokens") * input.inputTokenRate + value("preparationOutputTokens") * input.outputTokenRate) / 1e6);
    for (const key of ["voiceCloneAllocation", "voicePreviewAllocation", "scrapingAllocation", "postProcessingAllocation", "moderationAllocation"] as const) add(key, COST_FIELDS.find(field => field[0] === key)![1], value(key));
  }
  add("storage", "Retained inputs + outputs", value("storageGb") * value("retentionMonths") * value("storagePerGbMonth"));
  add("storage_operations", "Storage reads + writes", (value("writesPerVideo") * value("writesPerMillion") + value("readsPerVideo") * value("readsPerMillion")) / 1e6);
  add("delivery", "Delivery / downloads", value("deliveryGb") * value("deliveryPerGb"));
  add("worker", "Backend requests + CPU", (value("workerRequests") * value("workerPerMillionRequests") + value("workerCpuMs") * value("workerPerMillionCpuMs")) / 1e6);
  add("queue", "Queue delivery", value("queueRequests") * value("queuePerMillion") / 1e6);
  add("redis", "Redis variable allocation", value("redisAllocation"));
  add("database", "Database variable allocation", value("databaseAllocation"));
  add("other", "Other variable allocation", value("otherAllocation"));
  // Other allocations are already per delivered video. Applying a failure
  // multiplier to those again would double count their allocated losses.
  const retryableProviderCost = input.providerLines.reduce((sum, line) => sum + line.usd, 0);
  add("failure_reserve", "Failed paid provider attempts reserve", retryableProviderCost * value("failurePercent") / (100 - value("failurePercent")), "reserve");
  add("fixed", "Monthly fixed-cost allocation", value("monthlyFixedUsd") / (value("monthlyVideos") || 1));
  const baseCostUsd = lines.reduce((sum, line) => sum + line.usd, 0);
  const feeRatio = value("paymentPercent") / 100 + value("fxPercent") / 100 + value("paymentFixedPerTopup") / (value("typicalTopupUsd") || 1);
  // Processor fees are a fraction of the final selling price, not the cost.
  // Fixed top-up fees are allocated by wallet revenue, not charged per video.
  const cents = Math.max(0, Math.ceil(Math.max(input.minimumPriceCents, baseCostUsd * (1 + input.markupPercent / 100) / (1 - feeRatio) * 100) - 1e-9));
  if (!Number.isSafeInteger(cents) || cents < 0 || !Number.isFinite(baseCostUsd)) throw new Error("Calculated price exceeds safe monetary limits");
  const customerPriceUsd = cents / 100;
  const paymentCostUsd = customerPriceUsd * feeRatio;
  add("payment", "Allocated top-up processing + FX", paymentCostUsd);
  const missing = COST_FIELDS.filter(([key]) => s[key] === null).map(([key]) => key);
  return { version: 1, estimatedAt: new Date().toISOString(), lines, missing, baseCostUsd, paymentCostUsd,
    totalCostUsd: baseCostUsd + paymentCostUsd, customerPriceUsd,
    estimatedProfitUsd: customerPriceUsd - baseCostUsd - paymentCostUsd, complete: missing.length === 0, settings: s };
}
